import * as React from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  OutlinedInput,
  Skeleton,
  Stack,
  Typography,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Camera as CameraIcon, User as UserIcon, X as XIcon } from '@phosphor-icons/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'sonner';

import { useUser } from '@/hooks/v2/use-user';
import axios from '@/lib/v2/axios';
import { getS3PresignedUrlFromPath } from '@/utils/s3';
import { UserAvatar } from '@/components/common/user-avatar';

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  phoneNumber: Yup.string().nullable(),
});

export function UserDetails(): React.JSX.Element {
  const { user, checkSession } = useUser();
  const [isEditing, setIsEditing] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = React.useState(false);
  const [selectedAvatar, setSelectedAvatar] = React.useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [avatarUrl, setAvatarUrl] = React.useState<string | null>(null);

  // Debug: Log user data when it changes
  React.useEffect(() => {
    const getUserAvatar = async () => {
      if (user?.avatar?.path) {
        try {
          const url = await getS3PresignedUrlFromPath(user.avatar.path);
          setAvatarUrl(url);
        } catch (error) {
          console.error('Failed to get presigned URL:', error);
          // Fallback to direct path if presigned URL fails
          setAvatarUrl(user.avatar.path);
        }
      }
    };
    getUserAvatar();
  }, [user]);

  // Get avatar URL - either from preview, presigned URL, or direct path
  const displayAvatarUrl = avatarPreview || avatarUrl || user?.avatar?.path || undefined;
  const hasAvatar = Boolean(displayAvatarUrl);

  // Handle avatar file selection
  const handleAvatarSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image file size must be less than 5MB');
        return;
      }

      setSelectedAvatar(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle avatar upload
  const handleAvatarUpload = async () => {
    if (!selectedAvatar) return;

    try {
      setIsUploadingAvatar(true);
      
      const formData = new FormData();
      formData.append('avatar', selectedAvatar);
      
      // Use V2 avatar endpoint
      const response = await axios.post('/api/v2/auth/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        toast.success('Avatar uploaded successfully');
        
        console.log('Avatar upload response:', response.data);
        
        // Refresh user data to get the complete updated user object
        await checkSession();
        
        // Clear the preview since we now have the real avatar
        setAvatarPreview(null);
        
        // Reset avatar selection state
        setSelectedAvatar(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        console.log('Avatar uploaded successfully, user data refreshed');
      } else {
        toast.error(response.data.message || 'Failed to upload avatar');
      }
    } catch (error: any) {
      console.error('Avatar upload error:', error);
      toast.error('Failed to upload avatar. Please try again.');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  // Handle avatar removal
  const handleAvatarRemove = async () => {
    try {
      setIsUploadingAvatar(true);
      
      // Use V2 avatar removal endpoint
      const response = await axios.delete('/api/v2/auth/avatar');

      if (response.data.success) {
        toast.success('Avatar removed successfully');
        
        // Immediately clear the avatar preview
        setAvatarPreview(null);
        
        // Refresh user data to get the complete updated user object
        await checkSession();
        
        console.log('Avatar removed successfully');
      } else {
        toast.error(response.data.message || 'Failed to remove avatar');
      }
    } catch (error: any) {
      console.error('Avatar removal error:', error);
      toast.error('Failed to remove avatar. Please try again.');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  // Reset avatar selection
  const handleAvatarCancel = () => {
    setSelectedAvatar(null);
    setAvatarPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formik = useFormik({
    initialValues: {
      firstName: user?.first_name || '',
      lastName: user?.last_name || '',
      phoneNumber: user?.phone || '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        setIsSubmitting(true);
        
        console.log('Submitting profile update with values:', values);
        
        const payload = {
          first_name: values.firstName,
          last_name: values.lastName,
          phone: values.phoneNumber || null,
        };
        
        console.log('Sending payload to API:', payload);
        
        const response = await axios.put('/api/v2/auth/user', payload);

        console.log('Profile update response:', response);
        console.log('Response data:', response.data);

        // Handle successful response (200)
        if (response.status === 200) {
          toast.success('Profile updated successfully');
          setIsEditing(false);
          // Refresh user data to update the UI
          console.log('Calling checkSession to refresh user data...');
          await checkSession();
          console.log('checkSession completed');
        }
      } catch (error: any) {
        console.error('Profile update error:', error);
        
        // Handle different error scenarios
        if (error.response) {
          const { status, data } = error.response;
          
          switch (status) {
            case 400:
              // Bad request - validation errors or business logic errors
              const message = data?.message || 'Invalid request';
              toast.error(message);
              break;
            case 401:
              // Unauthorized
              toast.error('You are not authorized to perform this action');
              break;
            case 422:
              // Validation errors
              toast.error('Please check your input and try again');
              break;
            case 500:
              // Server error
              toast.error('Server error. Please try again later');
              break;
            default:
              // Other error statuses
              toast.error(data?.message || 'Failed to update profile');
          }
        } else if (error.request) {
          // Network error
          toast.error('Network error. Please check your connection');
        } else {
          // Other errors
          toast.error('An unexpected error occurred');
        }
      } finally {
        setIsSubmitting(false);
        setSubmitting(false);
      }
    },
  });

  const handleCancel = () => {
    setIsEditing(false);
    formik.resetForm();
    handleAvatarCancel();
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  if (!user) {
    return (
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Skeleton height={60} variant="rectangular" />
            <Skeleton height={40} variant="rectangular" />
            <Skeleton height={40} variant="rectangular" />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        avatar={
          <Avatar>
            <UserIcon fontSize="var(--Icon-fontSize)" />
          </Avatar>
        }
        title="Basic details"
      />
      <CardContent>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={3}>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              <Box
                sx={{
                  border: '1px dashed var(--mui-palette-divider)',
                  borderRadius: '50%',
                  display: 'inline-flex',
                  p: '4px',
                  position: 'relative',
                }}
              >
                <Box sx={{ borderRadius: 'inherit', position: 'relative' }}>
                  {/* Avatar Image */}
                  {isEditing ? (
                    <Avatar 
                      src={displayAvatarUrl} 
                      sx={{ 
                        '--Avatar-size': '100px',
                        cursor: 'pointer',
                      }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {!displayAvatarUrl && <UserIcon fontSize="var(--icon-fontSize-lg)" />}
                    </Avatar>
                  ) : (
                    <UserAvatar 
                      user={user} 
                      size="100px"
                    />
                  )}
                  
                  {/* Camera Overlay - Only show when editing */}
                  {isEditing && (
                    <Box
                      sx={{
                        alignItems: 'center',
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        borderRadius: 'inherit',
                        bottom: 0,
                        color: 'var(--mui-palette-common-white)',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'center',
                        left: 0,
                        opacity: 0,
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        zIndex: 1,
                        '&:hover': { opacity: 1 },
                      }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <CameraIcon fontSize="var(--icon-fontSize-md)" />
                        <Typography color="inherit" variant="subtitle2">
                          {hasAvatar ? 'Change' : 'Select'}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarSelect}
                style={{ display: 'none' }}
              />

              {/* Avatar Action Buttons */}
              <Stack spacing={1}>
                {isEditing && selectedAvatar && (
                  <>
                    <Button 
                      color="primary" 
                      size="small" 
                      variant="contained"
                      onClick={handleAvatarUpload}
                      disabled={isUploadingAvatar}
                    >
                      {isUploadingAvatar ? 'Uploading...' : 'Upload'}
                    </Button>
                    <Button 
                      color="secondary" 
                      size="small" 
                      variant="outlined"
                      onClick={handleAvatarCancel}
                      disabled={isUploadingAvatar}
                    >
                      Cancel
                    </Button>
                  </>
                )}
                
                {isEditing && hasAvatar && !selectedAvatar && (
                  <Button 
                    color="error" 
                    size="small" 
                    variant="outlined"
                    onClick={handleAvatarRemove}
                    disabled={isUploadingAvatar}
                  >
                    Remove
                  </Button>
                )}
              </Stack>
            </Stack>

            {!isEditing && (
              <Alert severity="info">
                Click "Edit" to modify your profile information and avatar.
              </Alert>
            )}

            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <FormControl sx={{ flex: '1 1 auto' }}>
                  <InputLabel>First name</InputLabel>
                  <OutlinedInput
                    disabled={!isEditing}
                    error={formik.touched.firstName ? Boolean(formik.errors.firstName) : undefined}
                    name="firstName"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.firstName}
                  />
                  {formik.touched.firstName && formik.errors.firstName ? <FormHelperText error>{formik.errors.firstName}</FormHelperText> : null}
                </FormControl>
                <FormControl sx={{ flex: '1 1 auto' }}>
                  <InputLabel>Last name</InputLabel>
                  <OutlinedInput
                    disabled={!isEditing}
                    error={formik.touched.lastName ? Boolean(formik.errors.lastName) : undefined}
                    name="lastName"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.lastName}
                  />
                  {formik.touched.lastName && formik.errors.lastName ? <FormHelperText error>{formik.errors.lastName}</FormHelperText> : null}
                </FormControl>
              </Stack>

              <FormControl disabled>
                <InputLabel>Email address</InputLabel>
                <OutlinedInput name="email" type="email" value={user?.email} />
                <FormHelperText>
                  Please <Link variant="inherit">contact us</Link> to change your email
                </FormHelperText>
              </FormControl>

              <FormControl>
                <InputLabel>Phone number</InputLabel>
                <OutlinedInput
                  disabled={!isEditing}
                  error={formik.touched.phoneNumber ? Boolean(formik.errors.phoneNumber) : undefined}
                  name="phoneNumber"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  placeholder="Enter your phone number (optional)"
                  value={formik.values.phoneNumber}
                />
                {formik.touched.phoneNumber && formik.errors.phoneNumber ? <FormHelperText error>{formik.errors.phoneNumber}</FormHelperText> : null}
                {!formik.errors.phoneNumber && (
                  <FormHelperText>Phone number is optional</FormHelperText>
                )}
              </FormControl>
            </Stack>
          </Stack>
        </form>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        {!isEditing ? (
          <Button color="secondary" onClick={handleEdit}>
            Edit
          </Button>
        ) : (
          <>
            <Button color="secondary" disabled={isSubmitting} onClick={handleCancel}>
              Cancel
            </Button>
            <Button 
              disabled={isSubmitting || !formik.isValid} 
              onClick={() => { formik.handleSubmit(); }}
              variant="contained"
            >
              {isSubmitting ? 'Saving...' : 'Save changes'}
            </Button>
          </>
        )}
      </CardActions>
    </Card>
  );
}
